"use client";

/**
 * HomeSidebar — wider navigation rail for the logged-in app shell.
 *
 * Mirrors the canvas SidebarMenu pattern (white background, icon buttons,
 * tooltips, DarkMenu for help + profile, NotificationBell at the bottom) but
 * exposes top-level navigation across the cross-project surfaces instead of
 * canvas-only actions:
 *
 *   1. Logo  → /
 *   2. Projects (current homepage) → /
 *   3. Orbit (cross-project people/companies DB) → /orbit
 *   4. Wrench (dev only) — toggles the Next.js dev indicator visibility,
 *      mirroring the canvas SidebarMenu.
 *   5. Help submenu (existing DarkMenu items)
 *   6. Notifications (existing bell)
 *   7. User profile (Clerk avatar) at the bottom-left
 *
 * The bottom-section order (Wrench → Help → Notifications → Profile) matches
 * the canvas `SidebarMenu` so the muscle memory is consistent across both
 * shells (LEV-700 follow-up).
 *
 * Active item is driven by `usePathname()` and announced via `aria-current`
 * (selection state, not a toggle). Unlike SidebarMenu this sidebar lives
 * everywhere except the canvas, so it does not own any project-scoped panel
 * state (Tasks, Labs, Share, Evaluation).
 */
import React from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import { useUser } from "@clerk/nextjs";
import {
  CircleHelp,
  FolderKanban,
  Keyboard,
  MessageSquarePlus,
  Orbit,
  Play,
  Wrench,
} from "lucide-react";

import { DarkMenu } from "@/components/molecules/ui/DarkMenu";
import { NotificationBell } from "@/components/molecules/notifications/NotificationBell";
import { NotificationSettingsModal } from "@/components/molecules/modals/NotificationSettingsModal";
import { KeyboardShortcutsModal } from "@/components/molecules/modals/KeyboardShortcutsModal";
import { TutorialPopup } from "@/components/molecules/modals/TutorialPopup";
import { openPostHogFeedback } from "@/lib/feedback";
import { trackFeedbackButtonClicked } from "@/lib/analytics/client";
import { cn } from "@/lib/utils";

type NavItem = {
  id: string;
  label: string;
  href: string;
  icon: React.ReactNode;
};

export const HOME_NAV_ITEMS: ReadonlyArray<NavItem> = [
  {
    id: "projects",
    label: "Projects",
    href: "/",
    icon: <FolderKanban className="h-5 w-5" aria-hidden />,
  },
  {
    id: "people",
    label: "Orbit",
    href: "/orbit",
    icon: <Orbit className="h-5 w-5" aria-hidden />,
  },
];

/**
 * Compare the current pathname against a navigation item's href.
 *
 * `/` only matches when the pathname is exactly `/` — otherwise every page
 * would highlight Projects. All other items match on prefix so nested routes
 * (e.g. a future `/orbit/[id]`) still highlight their parent tab.
 */
export function isHomeNavItemActive(pathname: string, href: string): boolean {
  if (href === "/") return pathname === "/";
  return pathname === href || pathname.startsWith(`${href}/`);
}

export function HomeSidebar() {
  const router = useRouter();
  const pathname = usePathname() ?? "/";
  const { user } = useUser();

  const userInitials = React.useMemo(() => {
    const first = user?.firstName?.[0]?.toUpperCase() ?? "";
    const last = user?.lastName?.[0]?.toUpperCase() ?? "";
    return `${first}${last}` || "?";
  }, [user?.firstName, user?.lastName]);
  const userAvatarUrl = user?.imageUrl ?? null;
  const userEmail = user?.primaryEmailAddress?.emailAddress;

  const [helpMenuOpen, setHelpMenuOpen] = React.useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = React.useState(false);
  const [notificationSettingsOpen, setNotificationSettingsOpen] =
    React.useState(false);
  const [keyboardShortcutsOpen, setKeyboardShortcutsOpen] =
    React.useState(false);
  const [showTutorial, setShowTutorial] = React.useState(false);

  const helpButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const profileButtonRef = React.useRef<HTMLButtonElement | null>(null);

  const isDev = process.env.NODE_ENV === "development";

  return (
    <>
      <nav
        aria-label="Primary"
        className="fixed z-sidebar left-0 w-16 bg-white border-r border-slate-200 flex flex-col top-[var(--maintenance-banner-h)] h-[calc(100vh-var(--maintenance-banner-h))]"
      >
        {/* Logo — links home rather than opening a submenu (per LEV-700 spec). */}
        <div className="p-3 flex flex-col items-center">
          <Link
            href="/"
            className="group relative inline-flex flex-col items-center p-2"
            aria-label="Lev home"
          >
            <Image
              src="/lev-icon-black.svg"
              alt="Lev"
              width={28}
              height={28}
              className="flex-shrink-0"
            />
          </Link>
        </div>

        {/* Primary nav (Projects / Orbit). */}
        <ul className="flex-1 flex flex-col items-center justify-start gap-2 pt-2 list-none m-0 p-0">
          {HOME_NAV_ITEMS.map((item) => {
            const active = isHomeNavItemActive(pathname, item.href);
            return (
              <li key={item.id}>
                <Link
                  href={item.href}
                  aria-current={active ? "page" : undefined}
                  aria-label={item.label}
                  className={cn(
                    "group relative flex items-center justify-center p-3 rounded-lg transition-colors",
                    active
                      ? "bg-slate-900 text-white"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900",
                  )}
                >
                  {item.icon}
                  <span
                    role="tooltip"
                    className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 whitespace-nowrap rounded bg-slate-900 px-2 py-1 text-xs text-white shadow-lg invisible group-hover:visible group-focus-within:visible"
                  >
                    {item.label}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>

        {/* Bottom: Dev tools (dev only), Help, Notifications, User.
            Order matches the canvas `SidebarMenu` bottom section so muscle
            memory is consistent across both shells. */}
        <div className="p-3 flex flex-col items-center gap-2">
          {isDev && (
            <button
              type="button"
              onClick={() =>
                document.body.classList.toggle("hide-dev-indicator")
              }
              className="group relative p-3 rounded-lg hover:bg-slate-100 transition-colors text-slate-600 hover:text-slate-900"
              aria-label="Toggle Next.js dev indicator"
              title="Toggle Next.js dev indicator"
            >
              <Wrench className="h-5 w-5" aria-hidden />
              <span
                role="tooltip"
                className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 whitespace-nowrap rounded bg-slate-900 px-2 py-1 text-xs text-white shadow-lg invisible group-hover:visible group-focus-within:visible"
              >
                Next.js (dev)
              </span>
            </button>
          )}
          <button
            ref={helpButtonRef}
            type="button"
            onClick={() => setHelpMenuOpen((v) => !v)}
            className="group relative p-3 rounded-lg hover:bg-slate-100 transition-colors text-slate-600 hover:text-slate-900"
            aria-label="Help"
            aria-haspopup="menu"
            aria-expanded={helpMenuOpen}
          >
            <CircleHelp className="h-5 w-5" aria-hidden />
            <span
              role="tooltip"
              className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 whitespace-nowrap rounded bg-slate-900 px-2 py-1 text-xs text-white shadow-lg invisible group-hover:visible group-focus-within:visible"
            >
              Help
            </span>
          </button>
          <DarkMenu
            items={[
              {
                id: "walkthrough",
                label: "Walkthrough",
                icon: <Play className="h-4 w-4" />,
              },
              {
                id: "keyboard-shortcuts",
                label: "Keyboard shortcuts",
                icon: <Keyboard className="h-4 w-4" />,
              },
              {
                id: "give-feedback",
                label: "Give feedback",
                icon: <MessageSquarePlus className="h-4 w-4" />,
              },
            ]}
            open={helpMenuOpen}
            onOpenChange={setHelpMenuOpen}
            onSelect={(id) => {
              setHelpMenuOpen(false);
              if (id === "walkthrough") setShowTutorial(true);
              if (id === "keyboard-shortcuts") setKeyboardShortcutsOpen(true);
              if (id === "give-feedback") {
                trackFeedbackButtonClicked({ location: "sidebar" });
                openPostHogFeedback({ position: "left" });
              }
            }}
            anchorRef={helpButtonRef}
            placement="sidebar-bottom"
            minWidth={180}
          />
          <NotificationBell
            onOpenSettings={() => setNotificationSettingsOpen(true)}
          />
          <button
            ref={profileButtonRef}
            type="button"
            onClick={() => setProfileMenuOpen((v) => !v)}
            className="h-10 w-10 overflow-hidden rounded-full bg-[#C084FC] text-white text-sm font-semibold shadow-md ring-2 ring-white/70 hover:ring-slate-200 transition-all"
            title="Profile"
            aria-label="Open profile menu"
            aria-haspopup="menu"
            aria-expanded={profileMenuOpen}
          >
            {userAvatarUrl ? (
              // eslint-disable-next-line @next/next/no-img-element -- remote Clerk avatar host, not configured for next/image
              <img
                src={userAvatarUrl}
                alt="Avatar"
                className="h-full w-full object-cover"
              />
            ) : (
              userInitials
            )}
          </button>
          <DarkMenu
            items={[
              ...(userEmail
                ? [
                    {
                      id: "email",
                      label: `Signed in as ${userEmail}`,
                      disabled: true,
                    },
                  ]
                : []),
              { id: "notification-settings", label: "Notification settings" },
              { id: "signout", label: "Sign out" },
            ]}
            open={profileMenuOpen}
            onOpenChange={setProfileMenuOpen}
            onSelect={(id) => {
              if (id === "notification-settings")
                setNotificationSettingsOpen(true);
              if (id === "signout") router.push("/sign-out");
            }}
            anchorRef={profileButtonRef}
            align="start"
            placement="sidebar-bottom"
            minWidth={220}
          />
        </div>
      </nav>

      <TutorialPopup
        forceShowVideo={showTutorial}
        onClose={() => setShowTutorial(false)}
      />
      <KeyboardShortcutsModal
        open={keyboardShortcutsOpen}
        onOpenChange={setKeyboardShortcutsOpen}
      />
      <NotificationSettingsModal
        open={notificationSettingsOpen}
        onOpenChange={setNotificationSettingsOpen}
      />
    </>
  );
}
